import { Modal } from '@/components';
import { useModal, useProfile, useTask } from '@/hooks';
import { useHandleCompleteTask } from '@/hooks/useHandleCompleteTask.hook';
import {
  TaskPublicDtoRead,
  useLazyTaskControllerGetTaskParticipantDataQuery,
} from '@/store/services/MidleNestApi.service';
import { Quests } from '@/types/quests.type';
import SubmitFileView from '@/views/tasks/submitFile.view';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
  completed?: boolean;
  task: TaskPublicDtoRead;
}

export default function AirdropTask({ completed, task }: IProps) {
  const { t } = useTranslation();
  const {
    showModal: showSubmitModal,
    hideModal: hideSubmitModal,
    id: submitModalId,
  } = useModal(`airdrop-task-submit-modal-${task.id}`);

  const { check, isWaiting, isCustomerWaiting } = useTask(String(task.id));
  const handleCompleteTask = useHandleCompleteTask({ task, showSubmitModal: showSubmitModal });
  const [getPhoto] = useLazyTaskControllerGetTaskParticipantDataQuery();
  const {
    profile: { user },
  } = useProfile();

  const [isPhotoUploaded, setIsPhotoUploaded] = useState<boolean>(false);

  const isPending = useMemo(
    () => isCustomerWaiting || isPhotoUploaded,
    [isCustomerWaiting, isPhotoUploaded],
  );

  useEffect(() => {
    if (task.action === 'uploadScreenShot')
      getPhoto(task.id as string).then(({ data }: any) => {
        if (data?.data.avatar.src) {
          setIsPhotoUploaded(true);
        }
      });
  }, [task, getPhoto]);

  const handleCheck = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();

    const actionType = task.action as Quests;
    if (user.id) {
      check(actionType)(task.id as string, () => {
        gtag('event', 'task-complete', {
          task: task.id,
          user: user?.id,
          project: task.customer.id,
          platform: task.platform,
          action: task.action,
          xp: String(task.xp),
        });
      });
    }
  };

  const buttonText = () => {
    if (completed) {
      return t('Completed');
    } else if (isPending) {
      return t('Pending');
    } else if (isWaiting) {
      return t('Verify');
    } else {
      return t('Complete');
    }
  };

  return (
    <div
      style={{
        background:
          'linear-gradient(135deg, transparent 1.5%, #FFDA68 calc(1%), #FFDA68 98.5%, transparent 5px)',
      }}
      className='w-full h-max lg:h-[74px] p-[1px]'
    >
      <div
        style={{
          background:
            'linear-gradient(135deg, transparent 1.5%, #191919 calc(1%), #191919 98.5%, transparent 5px)',
        }}
        className='flex flex-col lg:flex-row items-center justify-between pl-5 pr-2.5 w-full h-full pt-4 py-3 lg:py-0'
      >
        <div className='text-[#FFDA68] font-medium lg:font-bold text-xl lg:text-2xl'>
          {task.title.en}
        </div>

        <div className='flex flex-col lg:flex-row items-center mt-4 lg:mt-0'>
          <div className='lg:text-[#FFDA68] text-2xl lg:mr-7'>
            <span className='font-semibold'>{task.amp}</span> {t('PTS')}
          </div>

          <button
            onClick={(event) => {
              if (isWaiting) {
                handleCheck(event);
              } else {
                handleCompleteTask(event);
                // if (task.action !== 'uploadScreenShot') {
                // }
              }
            }}
            style={{
              background:
                'linear-gradient(135deg, transparent 5%, #FFDA68 calc(1%), #FFDA68 95%, transparent 5px)',
            }}
            disabled={isPending}
            className='w-40 lg:w-52 lg:h-14 h-9 hover:brightness-75 transition-all p-[1px] mt-2.5 lg:mt-0 disabled:opacity-50 disabled:pointer-events-none'
          >
            <div
              style={{
                background:
                  completed || !isWaiting || isPending
                    ? 'linear-gradient(135deg, transparent 5%, #191919 calc(1%), #191919 95%, transparent 5px)'
                    : 'none',
              }}
              className={classNames(
                'w-full h-full flex items-center justify-center text-lg lg:text-2xl font-semibold',
                completed || !isWaiting || isPending ? 'text-[#FFDA68]' : 'text-[#191919]',
              )}
            >
              {buttonText()}
            </div>
          </button>
        </div>
      </div>

      <Modal
        id={submitModalId}
        onClose={() => {
          const _t = localStorage.getItem('task-customer-waiting')?.split(',')?.includes(task.id);
          if (_t) location.reload();
          hideSubmitModal();
        }}
      >
        <i
          className='absolute right-4  top-4 icon-close text-2xl text-gray-400 cursor-pointer'
          onClick={() => {
            const _t = localStorage.getItem('task-customer-waiting')?.split(',')?.includes(task.id);
            if (_t) location.reload();
            hideSubmitModal();
          }}
        />

        <SubmitFileView
          taskId={task.id}
          visitUrl={task.visitUrl || ''}
          title={task.title.en}
          description={task.description?.tr}
          reloadOnVerify
          // avatar={logo}
          endDate={task.endDate}
        />
      </Modal>
    </div>
  );
}
